/**
 * Página de Billing (Paywall + Gerenciamento)
 * 
 * Estados:
 * - pending_payment → Exibir planos + botão "Assinar"
 * - trialing → Exibir "Trial ativo até DD/MM/AAAA" + botão "Assinar agora"
 * - active → Exibir "Assinatura ativa" + botão "Gerenciar cobrança"
 * - past_due/unpaid → Exibir alerta + botão "Atualizar pagamento"
 * - canceled → Exibir "Assinatura cancelada" + botão "Reativar"
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { config } from "@/lib/config";
import { useSession } from "@/state/session";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { PlanSelector } from "@/components/billing/PlanSelector";
import { CreditCard, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function Billing() {
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);

  const { user } = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadSubscriptionStatus();
    }
  }, [user]);

  // ✅ FASE 4: Detectar retorno do Stripe Checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      console.log('✅ Retornou do checkout:', sessionId);
      
      toast({
        title: "Pagamento confirmado!",
        description: "Atualizando status da assinatura...",
      });

      // Recarregar status
      setTimeout(() => {
        loadSubscriptionStatus();
      }, 2000);

      // Limpar URL
      window.history.replaceState({}, '', '/billing');
    }
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      // 1. Buscar tenant do usuário
      const { data: tenantUser, error: tenantUserError } = await supabase
        .from("tenant_users")
        .select("tenant_id")
        .eq("user_id", user!.id)
        .single();

      if (tenantUserError || !tenantUser) {
        throw new Error("Não foi possível encontrar seu negócio");
      }

      setTenantId(tenantUser.tenant_id);

      // 2. Buscar status da assinatura
      const { data: tenant, error: tenantError } = await supabase
        .from("tenant")
        .select("subscription_status, current_period_end, trial_end, stripe_customer_id")
        .eq("id", tenantUser.tenant_id)
        .single();

      if (tenantError || !tenant) {
        throw new Error("Não foi possível carregar status da assinatura");
      }

      setHasStripeCustomer(!!tenant.stripe_customer_id);

      // Calcular dias de trial restantes
      if (tenant.subscription_status === 'trialing' && tenant.trial_end) {
        const trialEnd = new Date(tenant.trial_end);
        const now = new Date();
        const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        setTrialDaysRemaining(daysLeft > 0 ? daysLeft : 0);
      }

      setSubscriptionStatus(tenant.subscription_status);
      setCurrentPeriodEnd(tenant.current_period_end);
    } catch (error: any) {
      console.error("❌ Erro ao carregar status:", error);
      toast({
        title: "Erro ao carregar informações",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPlan = async (planId: string, interval: "month" | "year") => {
    setIsCreatingCheckout(true);

    try {
      console.log("💳 Criando checkout para:", { planId, interval });

      toast({
        title: "Redirecionando...",
        description: "Aguarde enquanto preparamos seu checkout",
      });

      // ✅ Obter session do Supabase para o token JWT
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      // ✅ Chamar edge function com auth header (tenant_id resolvido server-side)
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          priceId: planId,
          successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/billing`,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (!data?.url) {
        throw new Error("URL de checkout não foi gerada");
      }

      console.log("✅ Checkout criado:", data.url);

      // Redirecionar para Stripe Checkout
      window.location.href = data.url;
    } catch (error: any) {
      console.error("❌ Erro ao criar checkout:", error);
      toast({
        title: "Erro ao processar pagamento",
        description: error.message || "Tente novamente mais tarde",
        variant: "destructive",
      });
      setIsCreatingCheckout(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      console.log("🔧 Abrindo Billing Portal...");

      // ✅ Obter session do Supabase para o token JWT
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      // ✅ Chamar edge function com auth header (tenant_id resolvido server-side)
      const { data, error } = await supabase.functions.invoke("stripe-portal", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (!data?.portal_url) {
        throw new Error("URL do portal não foi gerada");
      }

      console.log("✅ Portal criado:", data.portal_url);

      // Redirecionar para Stripe Billing Portal
      window.location.href = data.portal_url;
    } catch (error: any) {
      console.error("❌ Erro ao abrir portal:", error);
      toast({
        title: "Erro ao abrir gerenciamento",
        description: error.message || "Tente novamente mais tarde",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" text="Carregando informações..." />
        </div>
      </Layout>
    );
  }

  // Estado: pending_payment (usuário cadastrou mas não pagou)
  if (subscriptionStatus === "pending_payment") {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto space-y-8">
          {config.FEATURE_FLAGS.trialMode === "disabled" && (
            <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
              <Clock className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-900 dark:text-amber-100">
                <strong>⏳ Trial automático em ativação</strong>
                <br />
                Configure sua assinatura manualmente ou aguarde a ativação em breve.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="text-center space-y-2">
            <h1 className="text-4xl font-bold">🚀 Escolha seu plano</h1>
            <p className="text-muted-foreground">
              🎁 Comece com 14 dias grátis • Cancele quando quiser
            </p>
          </div>

          <PlanSelector
            onSelectPlan={handleSelectPlan}
            isLoading={isCreatingCheckout}
          />
        </div>
      </Layout>
    );
  }

  // Estado: trialing (trial ativo)
  if (subscriptionStatus === "trialing") {
    const isTrialExpiringSoon = trialDaysRemaining !== null && trialDaysRemaining <= 3;

    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-6 h-6 text-primary" />
                Trial ativo
              </CardTitle>
              <CardDescription>
                {trialDaysRemaining !== null && trialDaysRemaining > 0 ? (
                  <>
                    <span className="text-2xl font-bold text-primary">{trialDaysRemaining}</span> dias restantes
                  </>
                ) : (
                  "Seu trial expira hoje"
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isTrialExpiringSoon ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    <strong>⚠️ Seu trial expira em breve!</strong>
                    <br />
                    Adicione um método de pagamento para continuar usando o sistema após o trial.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <AlertDescription>
                    ✨ Aproveite todos os recursos gratuitamente até o fim do trial.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3">
                <Button onClick={() => navigate("/dashboard")}>
                  Explorar o sistema
                </Button>
                {hasStripeCustomer ? (
                  <Button 
                    variant={isTrialExpiringSoon ? "default" : "outline"} 
                    onClick={handleManageBilling}
                  >
                    {isTrialExpiringSoon ? "Adicionar pagamento agora" : "Gerenciar cobrança"}
                  </Button>
                ) : (
                  <Button 
                    variant={isTrialExpiringSoon ? "default" : "outline"} 
                    onClick={() => navigate("/billing")}
                  >
                    Assinar agora
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Estado: active (assinatura ativa)
  if (subscriptionStatus === "active") {
    // Se não tem stripe_customer_id, está em estado inconsistente - mostrar opção de assinar
    if (!hasStripeCustomer) {
      return (
        <Layout>
          <div className="max-w-6xl mx-auto space-y-8">
            <Card className="max-w-2xl mx-auto mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-primary" />
                  Bem-vindo!
                </CardTitle>
                <CardDescription>
                  Escolha um plano para começar
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert>
                  <AlertDescription>
                    Selecione um plano abaixo para ativar sua assinatura e começar a usar todos os recursos.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <div className="text-center space-y-2 mb-8">
              <h2 className="text-3xl font-bold">Escolha seu plano</h2>
              <p className="text-muted-foreground">
                Comece com 14 dias grátis • Cancele quando quiser
              </p>
            </div>

            <PlanSelector
              onSelectPlan={handleSelectPlan}
              isLoading={isCreatingCheckout}
            />
          </div>
        </Layout>
      );
    }

    const endDate = currentPeriodEnd ? format(new Date(currentPeriodEnd), "dd/MM/yyyy", { locale: ptBR }) : "—";

    // ✅ Se for Admin, mostrar planos para upgrade/downgrade
    const isAdmin = user?.role === 'admin';

    if (isAdmin) {
      return (
        <Layout>
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Card de status atual */}
            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  Assinatura ativa
                </CardTitle>
                <CardDescription>
                  Sua próxima cobrança será em {endDate}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertDescription>
                    🎉 Tudo certo! Sua assinatura está ativa e você tem acesso total ao sistema.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* Seção de upgrade/downgrade para Admin */}
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold">Alterar plano</h2>
              <p className="text-muted-foreground">
                Faça upgrade ou downgrade a qualquer momento
              </p>
            </div>

            <PlanSelector
              onSelectPlan={handleSelectPlan}
              isLoading={isCreatingCheckout}
            />
          </div>
        </Layout>
      );
    }

    // Para não-admins, mostrar apenas o card de assinatura ativa
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                Assinatura ativa
              </CardTitle>
              <CardDescription>
                Sua próxima cobrança será em {endDate}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  🎉 Tudo certo! Sua assinatura está ativa e você tem acesso total ao sistema.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Estado: past_due ou unpaid (pagamento atrasado)
  if (["past_due", "unpaid"].includes(subscriptionStatus || "")) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-destructive" />
                Pagamento pendente
              </CardTitle>
              <CardDescription>
                Há um problema com seu pagamento
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertTitle>Atenção!</AlertTitle>
                <AlertDescription>
                  Seu acesso ao sistema está temporariamente suspenso devido a um problema no pagamento.
                  {hasStripeCustomer ? (
                    <> Atualize suas informações de cobrança para restaurar o acesso.</>
                  ) : (
                    <> Complete seu cadastro escolhendo um plano para continuar.</>
                  )}
                </AlertDescription>
              </Alert>

              {hasStripeCustomer ? (
                <Button onClick={handleManageBilling} className="w-full">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Atualizar forma de pagamento
                </Button>
              ) : (
                <Button onClick={() => navigate("/billing")} className="w-full">
                  Escolher plano
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Estado: canceled (cancelada)
  if (subscriptionStatus === "canceled") {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto space-y-8">
          <Card className="max-w-2xl mx-auto mb-8">
            <CardHeader>
              <CardTitle>Assinatura cancelada</CardTitle>
              <CardDescription>
                Você cancelou sua assinatura
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>
                  Sua assinatura foi cancelada. Para voltar a usar o sistema, escolha um novo plano.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <div className="text-center space-y-2 mb-8">
            <h2 className="text-3xl font-bold">🔄 Escolha um novo plano</h2>
          </div>

          <PlanSelector
            onSelectPlan={handleSelectPlan}
            isLoading={isCreatingCheckout}
          />
        </div>
      </Layout>
    );
  }

  // Estado desconhecido
  return (
    <Layout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Status desconhecido</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Não foi possível determinar o status da sua assinatura.
            Entre em contato com o suporte.
          </p>
        </CardContent>
      </Card>
      </div>
    </Layout>
  );
}
